import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Home as HomeIcon, Camera, MapPin, MessageCircle, Tag, ShieldCheck, Calendar, ArrowRight, Sparkles, Truck, Upload, CheckCircle2, AlertCircle, EuroIcon, Trash2, Shield } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useCms } from "../lib/cms";
import { track } from "../lib/pixel";

// Empty placeholder — no fake reviews are shown until real customer reviews arrive.
const RealReviews = () => null;

const HOW_STEPS = [
  {
    icon: Upload,
    title: "Fotos & Adressen hochladen",
    text: "Sie wählen Kompletter Umzug, Kleinen Transport oder Entrümpelung, laden Fotos hoch und nennen uns die Adressen.",
  },
  {
    icon: EuroIcon,
    title: "Geschätzte Preisspanne erhalten",
    text: "Unser System zeigt Ihnen direkt eine faire Preisspanne — basierend auf Distanz, Etage, Aufzug und Inhalt.",
  },
  {
    icon: MessageCircle,
    title: "Finales Angebot per WhatsApp",
    text: "Nach kurzer Prüfung Ihrer Fotos bestätigen wir den finalen Preis und Termin direkt per WhatsApp.",
  },
];

const PAIN_POINTS = [
  "Unklare Preise & versteckte Kosten",
  "Lange Wartezeiten auf Rückmeldungen",
  "Kurzfristig keinen Termin bekommen",
  "Schwere Möbel ohne Aufzug",
  "Endloses Telefonieren statt schneller Klärung",
];

const LEUKAS_SOLUTIONS = [
  "Schnelle WhatsApp-Angebote — Antwort in Minuten",
  "Faire, transparente Preisspanne — bevor Sie buchen",
  "Fotos statt langer Telefonate",
  "Aachen, Köln, Düsseldorf — wir kennen die Region",
  "Flexible kleine Transporte, Komplettumzüge und Entrümpelung",
];

const REGION_COLS = [
  {
    title: "Aachen & Umgebung",
    places: ["Aachen", "Würselen", "Herzogenrath", "Alsdorf", "Stolberg", "Eschweiler", "Baesweiler", "Düren", "Kornelimünster", "Brand", "Burtscheid"],
  },
  {
    title: "Köln & Umgebung",
    places: ["Köln", "Ehrenfeld", "Nippes", "Lindenthal", "Sülz", "Deutz", "Kalk", "Porz", "Mülheim", "Rodenkirchen", "Leverkusen", "Bergisch Gladbach", "Frechen", "Hürth", "Pulheim", "Brühl"],
  },
  {
    title: "Düsseldorf & Umgebung",
    places: ["Düsseldorf", "Bilk", "Oberkassel", "Pempelfort", "Flingern", "Derendorf", "Gerresheim", "Benrath", "Neuss", "Ratingen", "Hilden", "Meerbusch", "Erkrath"],
  },
];

const FULL_TIERS = [
  { title: "1–2 Zimmer", price: "ab 490 €" },
  { title: "3 Zimmer", price: "ab 890 €" },
  { title: "4 Zimmer / Haus", price: "ab 1.290 €" },
];

const TRUST_ITEMS = [
  { icon: MapPin, title: "Feste Teams in Aachen, Köln & Düsseldorf", text: "Wir kennen die Region und die Wege." },
  { icon: MessageCircle, title: "Schnelle WhatsApp-Angebote", text: "Antwort innerhalb weniger Minuten." },
  { icon: ShieldCheck, title: "Sorgfältiger Transport", text: "Mit Decken & Erfahrung verpackt." },
  { icon: Shield, title: "Transportversichert", text: "Ihre Möbel sind während des gesamten Umzugs versichert." },
];

const FAQS = [
  {
    q: "Wie genau ist der Preis?",
    a: "Der angezeigte Preis ist ein geschätzter Preisbereich, basierend auf Adresse, Etage, Aufzug und Ihren Fotos. Der finale Festpreis wird nach kurzer Prüfung der Fotos über WhatsApp bestätigt.",
  },
  {
    q: "Muss ich Fotos hochladen?",
    a: "Nein, aber sehr empfohlen. Je mehr Fotos Sie hochladen, desto genauer wird Ihr individuelles Angebot.",
  },
  {
    q: "Wann bekomme ich den finalen Preis?",
    a: "Sobald wir Ihre Fotos kurz geprüft haben, erhalten Sie den finalen Preis direkt per WhatsApp – meist innerhalb weniger Minuten zu unseren Geschäftszeiten.",
  },
  {
    q: "Gibt es kurzfristige Termine?",
    a: "Ja. Wir versuchen auch kurzfristige Termine möglich zu machen — auch samstags. Schreiben Sie uns einfach Ihr Wunschdatum.",
  },
  {
    q: "Transportiert ihr auch einzelne Möbelstücke?",
    a: "Selbstverständlich – Waschmaschine, Sofa, Bett, Schrank, Kartons oder einzelne Möbel: Wählen Sie einfach „Kleine Transporte“.",
  },
  {
    q: "Ist der Festpreis wirklich verbindlich?",
    a: "Ja. Nach Prüfung Ihrer Fotos und Angaben nennen wir Ihnen einen verbindlichen Festpreis. Es gibt keine versteckten Zusatzkosten am Umzugstag, solange sich der Umfang nicht ändert.",
  },
  {
    q: "Sind meine Möbel während des Transports versichert?",
    a: "Ja. Ihre Möbel sind während des gesamten Umzugs bis zur besenreinen Übergabe transportversichert.",
  },
  {
    q: "Bauen Sie Möbel ab und wieder auf?",
    a: "Auf Wunsch bauen wir Schränke, Betten und weitere Möbel bei Ihnen ab und am Zielort wieder auf. Sagen Sie uns einfach vorab Bescheid, damit wir das im Angebot berücksichtigen können.",
  },
];

export default function Home() {
  const { cms } = useCms();
  const heroImages = (cms.images?.hero_strip?.length ? cms.images.hero_strip : [
    "https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&w=900",
    "https://images.pexels.com/photos/7464672/pexels-photo-7464672.jpeg?auto=compress&cs=tinysrgb&w=900",
    "https://images.pexels.com/photos/7464716/pexels-photo-7464716.jpeg?auto=compress&cs=tinysrgb&w=900",
  ]);

  useEffect(() => {
    document.title = "Umzugsunternehmen Aachen, Köln & Düsseldorf | Festpreis | Leukas Umzug";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Umzug, Möbeltransport und Entrümpelung in Aachen, Köln, Düsseldorf und Umgebung. Festpreis-Garantie, versicherter Transport, Angebot in 2 Minuten.");
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/40 via-white to-white" />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-amber-200/30 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-slate-200/40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-14 pb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-soft" data-testid="hero-badge">
              <Sparkles size={14} className="text-brand-amberDark" />
              <span>Aachen · Köln · Düsseldorf · Festpreis-Angebot in 2 Minuten</span>
            </div>
            <h1 className="mt-4 text-[28px] leading-[1.1] sm:text-5xl lg:text-6xl font-heading font-extrabold tracking-tight text-slate-900" data-testid="hero-headline">
              Umzug, Transport & Entrümpelung in <span className="text-brand-amberDark">Aachen, Köln und Düsseldorf</span>
            </h1>
            <p className="mt-3 sm:mt-5 text-base sm:text-xl text-slate-600 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
              Festpreis-Angebot, versicherter Transport, feste Termine. Ihre Anfrage ist in zwei Minuten gestellt.
            </p>
          </div>

          {/* Three service cards */}
          <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-5">
            <Link
              to="/angebot?type=full"
              onClick={() => track({ custom: "CompleteMoveSelected" })}
              className="group relative rounded-2xl sm:rounded-3xl bg-brand-navy text-white p-3 sm:p-7 shadow-floating hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
              data-testid="card-full"
            >
              <div className="w-9 h-9 sm:w-14 sm:h-14 rounded-lg sm:rounded-2xl bg-white/10 text-brand-amber grid place-items-center group-hover:bg-brand-amber group-hover:text-brand-navy transition-colors">
                <HomeIcon className="w-4 h-4 sm:w-7 sm:h-7" />
              </div>
              <h3 className="mt-2 sm:mt-4 text-[13px] sm:text-2xl font-heading font-bold leading-tight">Kompletter Umzug</h3>
              <p className="mt-1 text-[10px] sm:text-[14px] leading-snug text-slate-300 hidden sm:block">Wohnung · Haus · Büro · Mehrere Zimmer</p>
              <p className="mt-1 text-[10px] leading-tight text-slate-300 sm:hidden">Wohnung · Haus · Büro</p>
              <div className="mt-3 sm:mt-6 inline-flex items-center justify-center gap-1 rounded-lg sm:rounded-xl bg-brand-amber text-brand-navy px-2 sm:px-5 py-2 sm:py-3 text-[11px] sm:text-sm font-bold shadow-soft group-hover:bg-amber-300 transition-colors text-center" data-testid="cta-full">
                <span className="hidden sm:inline">Umzug planen</span>
                <span className="sm:hidden">Planen</span>
                <ArrowRight size={12} className="hidden sm:inline" />
              </div>
            </Link>

            <Link
              to="/angebot?type=small"
              onClick={() => track({ custom: "SmallTransportSelected" })}
              className="group relative rounded-2xl sm:rounded-3xl bg-white border border-slate-200 p-3 sm:p-7 shadow-soft hover:shadow-floating hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
              data-testid="card-small"
            >
              <div className="w-9 h-9 sm:w-14 sm:h-14 rounded-lg sm:rounded-2xl bg-amber-50 text-brand-amberDark grid place-items-center group-hover:bg-brand-amber group-hover:text-white transition-colors">
                <Package className="w-4 h-4 sm:w-7 sm:h-7" />
              </div>
              <h3 className="mt-2 sm:mt-4 text-[13px] sm:text-2xl font-heading font-bold text-slate-900 leading-tight">Kleine Transporte</h3>
              <p className="mt-1 text-[10px] sm:text-[14px] leading-snug text-slate-600 hidden sm:block">Waschmaschine · Sofa · Bett · Schrank · Einzelstücke</p>
              <p className="mt-1 text-[10px] leading-tight text-slate-600 sm:hidden">Sofa · Waschm. · Möbel</p>
              <div className="mt-3 sm:mt-6 inline-flex items-center justify-center gap-1 rounded-lg sm:rounded-xl bg-brand-navy text-white px-2 sm:px-5 py-2 sm:py-3 text-[11px] sm:text-sm font-semibold shadow-soft group-hover:bg-slate-800 transition-colors text-center" data-testid="cta-small">
                <span className="hidden sm:inline">Transport starten</span>
                <span className="sm:hidden">Starten</span>
                <ArrowRight size={12} className="hidden sm:inline" />
              </div>
            </Link>

            <Link
              to="/entruempelung"
              onClick={() => track({ custom: "ClearanceSelected" })}
              className="group relative rounded-2xl sm:rounded-3xl bg-white border border-slate-200 p-3 sm:p-7 shadow-soft hover:shadow-floating hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
              data-testid="card-clearance"
            >
              <div className="w-9 h-9 sm:w-14 sm:h-14 rounded-lg sm:rounded-2xl bg-slate-100 text-slate-700 grid place-items-center group-hover:bg-brand-navy group-hover:text-white transition-colors">
                <Trash2 className="w-4 h-4 sm:w-7 sm:h-7" />
              </div>
              <h3 className="mt-2 sm:mt-4 text-[13px] sm:text-2xl font-heading font-bold text-slate-900 leading-tight">Entrümpelung</h3>
              <p className="mt-1 text-[10px] sm:text-[14px] leading-snug text-slate-600 hidden sm:block">Wohnung · Haus · Keller · Nachlass · Besenrein</p>
              <p className="mt-1 text-[10px] leading-tight text-slate-600 sm:hidden">Wohnung · Keller · Nachlass</p>
              <div className="mt-3 sm:mt-6 inline-flex items-center justify-center gap-1 rounded-lg sm:rounded-xl bg-brand-navy text-white px-2 sm:px-5 py-2 sm:py-3 text-[11px] sm:text-sm font-semibold shadow-soft group-hover:bg-slate-800 transition-colors text-center" data-testid="cta-clearance">
                <span className="hidden sm:inline">Angebot anfragen</span>
                <span className="sm:hidden">Angebot</span>
                <ArrowRight size={12} className="hidden sm:inline" />
              </div>
            </Link>
          </div>

          {/* Trust chips row — factual only, no rating, no invented counts */}
          <div className="mt-6 sm:mt-10 rounded-2xl sm:rounded-3xl bg-white border border-slate-200 shadow-soft px-3 sm:px-6 py-3 sm:py-4" data-testid="trust-chips">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] sm:text-sm text-slate-700">
              <span className="inline-flex items-center gap-1.5 font-semibold"><Tag size={14} className="text-brand-amberDark" /> Festpreis-Garantie</span>
              <span className="inline-flex items-center gap-1.5"><Shield size={14} className="text-slate-500" /> Transportversichert</span>
              <span className="inline-flex items-center gap-1.5"><MessageCircle size={14} className="text-green-600" /> Antwort in Minuten</span>
              <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-slate-500" /> Termine auch samstags</span>
            </div>
          </div>

          {/* Hero photo strip */}
          <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-3" data-testid="hero-visuals">
            {heroImages.slice(0, 3).map((src, idx) => (
              <div key={idx} className={`relative aspect-[4/3] sm:aspect-[5/3] rounded-2xl overflow-hidden ${idx === 1 ? "bg-brand-navy" : "bg-slate-200"}`}>
                <img src={src} alt="Moving" loading="lazy" className={`w-full h-full object-cover ${idx === 1 ? "opacity-90" : ""}`} />
                {idx === 1 && (
                  <div className="absolute top-2 left-2 sm:top-3 sm:left-3 rounded-lg bg-brand-amber text-brand-navy px-2 py-1 text-[10px] sm:text-xs font-extrabold shadow-soft">
                    LEUKAS
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-2 sm:p-3 bg-gradient-to-t from-black/60 to-transparent text-[10px] sm:text-xs font-semibold text-white">
                  {idx === 0 ? "Möbeltransport · Aachen" : idx === 1 ? "Sorgfältig verpackt" : "Pünktlich · Freundlich"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* So funktioniert's */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="how-section">
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">In 3 einfachen Schritten</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">So funktioniert's</h2>
          <p className="mt-2 text-slate-600 max-w-xl mx-auto">Vom Foto zum Festpreis — in wenigen Minuten und ohne Stress.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {HOW_STEPS.map((s, i) => (
            <div key={i} className="relative rounded-3xl bg-white border border-slate-200 p-6 sm:p-7 shadow-soft hover:shadow-floating transition-shadow">
              <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                Schritt {i + 1}
              </div>
              <div className="w-12 h-12 rounded-2xl bg-amber-50 text-brand-amberDark grid place-items-center mb-4">
                <s.icon size={22} />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-[15px] text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service area */}
      <section id="einsatzgebiet" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="region-section">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Wo wir arbeiten</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Unser Einsatzgebiet</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {REGION_COLS.map((col, i) => (
            <div key={i} className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft" data-testid={`region-col-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-amber-50 text-brand-amberDark grid place-items-center"><MapPin size={16} /></span>
                <h3 className="font-heading font-bold text-slate-900 text-base">{col.title}</h3>
              </div>
              <p className="text-[14px] sm:text-[15px] text-slate-600 leading-relaxed">
                {col.places.join(", ")}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-slate-600 max-w-2xl mx-auto">
          Ihr Ort ist nicht dabei? Fragen Sie uns – wir sind in der ganzen Region unterwegs.
        </p>
      </section>

      {/* AI / Camera scanner section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white p-6 sm:p-12 shadow-floating" data-testid="scan-section">
          <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-amber-400/5 blur-3xl" />
          <div className="grid md:grid-cols-[auto,1fr,auto] gap-6 sm:gap-10 items-center">
            <div className="relative mx-auto md:mx-0">
              <span className="absolute inset-0 rounded-3xl bg-brand-amber/30 animate-pulse-ring" />
              <span className="absolute inset-2 rounded-3xl bg-brand-amber/20 animate-pulse-ring" style={{ animationDelay: "0.6s" }} />
              <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-3xl bg-brand-amber text-brand-navy grid place-items-center shadow-glow">
                <Camera size={56} strokeWidth={2.2} />
              </div>
            </div>
            <div className="text-center md:text-left">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-300">Kamera · Schnelltest</div>
              <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-heading font-bold leading-tight">
                Was könnte Ihr Transport kosten?
              </h2>
              <p className="mt-3 text-slate-300 max-w-xl leading-relaxed mx-auto md:mx-0">
                Foto hochladen oder Kamera öffnen und sofort eine Preis-Schätzung erhalten.
              </p>
              <p className="mt-1 text-xs text-amber-300/90 font-semibold">Nur geschätzte Preisspanne</p>
            </div>
            <Link
              to="/scan"
              onClick={() => track({ custom: "ScannerOpened" })}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-amber text-brand-navy px-6 py-4 text-base font-bold shadow-soft hover:bg-amber-300 transition-colors whitespace-nowrap mx-auto md:mx-0"
              data-testid="scan-cta"
            >
              <Camera size={18} /> Jetzt Preis prüfen
            </Link>
          </div>
        </div>
      </section>

      {/* Real reviews placeholder — nothing until we have real reviews */}
      <RealReviews />

      {/* Pain points vs Leukas solutions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="problems-section">
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Probleme & Lösungen</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Diese Probleme kennen viele beim Umzug</h2>
          <p className="mt-2 text-slate-600 max-w-2xl mx-auto">Wir haben Umzüge neu gedacht — transparent, schnell und ohne Telefon-Marathon.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-7 shadow-soft">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest">Üblich</div>
            <h3 className="mt-3 text-xl font-heading font-bold text-slate-900">Was Kunden frustriert</h3>
            <ul className="mt-4 space-y-3">
              {PAIN_POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                  <span className="w-6 h-6 rounded-full bg-red-50 text-red-600 grid place-items-center shrink-0 mt-0.5"><AlertCircle size={14} /></span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-brand-navy text-white p-6 sm:p-7 shadow-floating">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 text-brand-amber px-3 py-1 text-[11px] font-bold uppercase tracking-widest">Bei Leukas</div>
            <h3 className="mt-3 text-xl font-heading font-bold">So lösen wir das</h3>
            <ul className="mt-4 space-y-3">
              {LEUKAS_SOLUTIONS.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-slate-200">
                  <span className="w-6 h-6 rounded-full bg-amber-400/20 text-brand-amber grid place-items-center shrink-0 mt-0.5"><CheckCircle2 size={14} /></span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Preise — restructured: Group A (Kompletter Umzug) FIRST, Group B (Kleine Transporte) */}
      <section id="preise" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="prices-section">
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Preise</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Was kostet Ihr Umzug?</h2>
          <p className="mt-2 text-slate-600 max-w-2xl mx-auto">Transparente Richtpreise — der verbindliche Festpreis kommt nach kurzer Prüfung per WhatsApp.</p>
        </div>

        {/* Group A — Kompletter Umzug */}
        <div className="mb-8" data-testid="prices-group-full">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-lg bg-brand-navy text-white grid place-items-center"><HomeIcon size={16} /></span>
            <h3 className="font-heading font-bold text-slate-900 text-lg sm:text-xl">Kompletter Umzug</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {FULL_TIERS.map((t, i) => (
              <div key={i} className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft hover:shadow-floating transition-shadow" data-testid={`price-full-${i}`}>
                <div className="text-[11px] font-bold uppercase tracking-widest text-brand-amberDark">{t.title}</div>
                <div className="mt-3 font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">{t.price}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-600">
            Richtwerte. Ihr verbindlicher Festpreis hängt von Etage, Aufzug, Entfernung und Zusatzleistungen ab — kostenlos und unverbindlich nach Ihrer Anfrage.
          </p>
        </div>

        {/* Group B — Kleine Transporte */}
        <div data-testid="prices-group-small">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-lg bg-amber-50 text-brand-amberDark grid place-items-center"><Package size={16} /></span>
            <h3 className="font-heading font-bold text-slate-900 text-lg sm:text-xl">Kleine Transporte</h3>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-brand-amberDark">Einzelne Möbelstücke</div>
                <p className="mt-1 text-slate-700 text-[15px]">Waschmaschine, Sofa, Kartons – innerhalb der Region.</p>
              </div>
              <div className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">60 € – 150 €</div>
            </div>
            <p className="mt-3 text-sm text-slate-500">Längere Strecken auf Anfrage.</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-900 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <p>Alle Preise sind geschätzte Richtwerte. Der endgültige Festpreis wird nach kurzer Prüfung Ihrer Fotos über WhatsApp bestätigt.</p>
        </div>
      </section>

      {/* Warum Leukas Umzug */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Warum Leukas Umzug</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">
            Vertrauen, das sich anfühlt wie zu Hause
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="trust-grid">
          {TRUST_ITEMS.map((t, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-soft hover:shadow-floating transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-brand-amberDark grid place-items-center mb-3">
                <t.icon size={20} />
              </div>
              <div className="font-heading font-semibold text-slate-900 text-base">{t.title}</div>
              <div className="text-sm text-slate-600 mt-1 leading-snug">{t.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="text-center mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Häufige Fragen</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Alles, was Sie wissen müssen</h2>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 shadow-soft p-2 sm:p-3" data-testid="faq-wrap">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b last:border-b-0 border-slate-100">
                <AccordionTrigger className="px-4 py-4 text-left font-heading font-semibold text-slate-900 hover:no-underline" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 text-slate-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Final CTA */}
        <div className="mt-12 rounded-3xl bg-brand-navy text-white p-8 sm:p-10 text-center shadow-floating">
          <h3 className="text-2xl sm:text-3xl font-heading font-bold">Bereit für Ihren Umzug?</h3>
          <p className="mt-2 text-slate-300">In 2 Minuten Angebot anfragen – Antwort per WhatsApp.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/angebot?type=full" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-amber text-brand-navy px-6 py-3 font-bold hover:bg-amber-300 transition-colors" data-testid="cta-final-full">
              <HomeIcon size={18} /> Kompletter Umzug
            </Link>
            <Link to="/angebot?type=small" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 px-6 py-3 font-semibold transition-colors" data-testid="cta-final-small">
              <Package size={18} /> Kleiner Transport
            </Link>
            <Link to="/entruempelung" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 px-6 py-3 font-semibold transition-colors" data-testid="cta-final-clearance">
              <Trash2 size={18} /> Entrümpelung
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
